const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const FORM_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const FORM_ID_EXTRACT_PATTERN = /([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})/i;
const RECENT_SUBMIT_TTL_MS = 30000;
const HUBSPOT_ALLOWED_MESSAGE_HOST_SUFFIX = '.hsforms.com';

const state = {
	runtimeModulePromise: null,
	inFlightSubmitKeys: new Set(),
	recentlyHandledSubmitAt: new Map(),
};

const modalResetState = new WeakMap();

const SUBMISSION_VALUE_ALIASES = {
	email: [ 'email' ],
	firstname: [ 'firstname', 'first_name', 'first-name', 'first name' ],
	lastname: [ 'lastname', 'last_name', 'last-name', 'last name' ],
	company: [ 'company', 'company_name', 'company-name', 'company name' ],
};

/**
 * Returns true when message event comes from the current window (classic v2.js
 * embed posts from page context) or from a HubSpot forms origin.
 *
 * @param {MessageEvent} event PostMessage event.
 * @return {boolean}
 */
const isTrustedHubspotMessageEvent = ( event ) => {

	if ( event?.source === window ) {

		return true;

	}

	const origin = `${ event?.origin || '' }`.trim();
	if ( ! origin ) {

		return false;

	}

	try {

		const parsed = new URL( origin );
		if ( parsed.protocol !== 'https:' ) {

			return false;

		}

		const hostname = `${ parsed.hostname || '' }`.toLowerCase();
		return hostname === 'hsforms.com' || hostname.endsWith( HUBSPOT_ALLOWED_MESSAGE_HOST_SUFFIX );

	} catch ( error ) {

		return false;

	}

};

/**
 * @typedef {Object} CalendarRoutingSubmission
 * @property {string} formId
 * @property {string} email
 * @property {string} firstname
 * @property {string} lastname
 * @property {string} company
 * @property {'inline'|'modal'} sourceType
 * @property {HTMLElement|null} sourceModal
 * @property {HTMLElement|null} sourceContainer
 */

/**
 * Returns lightweight runtime config.
 *
 * @return {{enabled:boolean, endpoint:string, timeoutMs:number, fallbackCalendarBaseUrl:string, allowedFormIds:string[]}}
 */
const getRuntimeConfig = () => {

	const config = window.themeVars?.demoCalendarRouting;
	const allowedFormIds = Array.isArray( config?.allowedFormIds ) ? config.allowedFormIds : [];

	return {
		enabled: Boolean( config?.enabled ),
		endpoint: `${ config?.endpoint || '' }`.trim(),
		timeoutMs: Number.isFinite( Number( config?.timeoutMs ) ) ? Number( config.timeoutMs ) : 15000,
		fallbackCalendarBaseUrl: `${ config?.fallbackCalendarBaseUrl || '' }`.trim(),
		allowedFormIds: allowedFormIds
			.map( ( id ) => `${ id || '' }`.trim().toLowerCase() )
			.filter( ( id ) => FORM_ID_PATTERN.test( id ) ),
	};

};

/**
 * Returns current source context. If a modal is currently open, flow is modal-based.
 *
 * @param {EventTarget|null} eventTarget Event target from submit callback.
 * @return {{sourceType:'inline'|'modal', sourceModal:HTMLElement|null, sourceContainer:HTMLElement|null, modalCalendarEnabled:boolean}}
 */
const resolveSourceContext = ( eventTarget = null ) => {

	let modalElement = null;
	if ( eventTarget instanceof HTMLElement ) {

		modalElement = eventTarget.closest( '.b-modal[data-modal="true"]' );

	}

	if ( ! modalElement ) {

		modalElement = document.querySelector( '.b-modal[data-modal="true"][aria-hidden="false"]' );

	}

	if ( modalElement instanceof HTMLElement ) {

		const sourceContainer = modalElement.querySelector( '.b-modal__content' );
		const dataShowCalendar = `${ modalElement.dataset.showCalendarForm || '' }`.toLowerCase();

		return {
			sourceType: 'modal',
			sourceModal: modalElement,
			sourceContainer: sourceContainer instanceof HTMLElement ? sourceContainer : null,
			modalCalendarEnabled: dataShowCalendar === 'true',
		};

	}

	return {
		sourceType: 'inline',
		sourceModal: null,
		sourceContainer: null,
		modalCalendarEnabled: true,
	};

};

/**
 * Normalizes potentially prefixed HubSpot keys (eg. 0-1/email => email).
 *
 * @param {string} rawKey Input key.
 * @return {string}
 */
const normalizeFieldName = ( rawKey ) => {

	const value = `${ rawKey || '' }`.trim().toLowerCase();
	if ( value.includes( '/' ) ) {

		return value.split( '/' ).pop() || value;

	}

	return value;

};

/**
 * Converts mixed HubSpot field value shapes to one plain string.
 *
 * @param {unknown} rawValue Raw field value.
 * @return {string}
 */
const normalizeSubmissionValue = ( rawValue ) => {

	if ( rawValue === null || typeof rawValue === 'undefined' ) {

		return '';

	}

	if ( typeof rawValue === 'string' || typeof rawValue === 'number' || typeof rawValue === 'boolean' ) {

		return `${ rawValue }`.trim();

	}

	if ( Array.isArray( rawValue ) ) {

		return rawValue
			.map( ( item ) => normalizeSubmissionValue( item ) )
			.filter( Boolean )
			.join( ', ' )
			.trim();

	}

	if ( typeof rawValue === 'object' ) {

		if ( 'value' in rawValue ) {

			return normalizeSubmissionValue( rawValue.value );

		}

		if ( 'fieldValue' in rawValue ) {

			return normalizeSubmissionValue( rawValue.fieldValue );

		}

	}

	return '';

};

/**
 * Tries to normalize form id and extract UUID-like value from mixed strings.
 *
 * @param {string} rawFormId Raw form identifier.
 * @return {string}
 */
const normalizeFormId = ( rawFormId ) => {

	const value = `${ rawFormId || '' }`.trim().toLowerCase();
	if ( FORM_ID_PATTERN.test( value ) ) {

		return value;

	}

	const match = value.match( FORM_ID_EXTRACT_PATTERN );
	if ( ! match || ! match[1] ) {

		return '';

	}

	return match[1].toLowerCase();

};

/**
 * Converts multiple HubSpot value shapes into one plain object.
 *
 * @param {unknown} rawValues HubSpot values.
 * @return {Record<string,string>}
 */
const normalizeSubmissionValues = ( rawValues ) => {

	const values = {};
	if ( Array.isArray( rawValues ) ) {

		rawValues.forEach( ( item ) => {

			if ( ! item || typeof item !== 'object' ) {

				return;

			}

			const key = normalizeFieldName( item.name || item.fieldName || item.key || '' );
			const rawValue = normalizeSubmissionValue( item.value ?? item.fieldValue ?? '' );
			if ( key ) {

				values[ key ] = rawValue;

			}

		} );

		return values;

	}

	if ( rawValues && typeof rawValues === 'object' ) {

		Object.entries( rawValues ).forEach( ( [ key, value ] ) => {

			const normalizedKey = normalizeFieldName( key );
			if ( ! normalizedKey ) {

				return;

			}

			values[ normalizedKey ] = normalizeSubmissionValue( value );

		} );

	}

	return values;

};

/**
 * Returns values from HubSpotFormsV4 for the current event/form instance.
 *
 * @param {CustomEvent} event HubSpot success event.
 * @param {Record<string,unknown>} detail Event detail payload.
 * @return {Record<string,string>}
 */
const getHubSpotV4SubmissionValues = async ( event, detail ) => {

	const api = window.HubSpotFormsV4;
	if ( ! api || typeof api.getFormFromEvent !== 'function' ) {

		return {};

	}

	try {

		const form = api.getFormFromEvent( event );
		if ( ! form || typeof form.getFormFieldValues !== 'function' ) {

			return normalizeSubmissionValues(
				detail?.submissionValues || detail?.values || detail?.data || detail?.formData || []
			);

		}

		const rawValues = await Promise.resolve( form.getFormFieldValues() || {} );
		const normalizedValues = normalizeSubmissionValues( rawValues );

		if ( Object.values( normalizedValues ).some( ( value ) => `${ value || '' }`.trim() !== '' ) ) {

			return normalizedValues;

		}

		if ( typeof form.getFieldValue === 'function' ) {

			const valuesByField = {};
			const fieldAliases = [
				...SUBMISSION_VALUE_ALIASES.email,
				...SUBMISSION_VALUE_ALIASES.firstname,
				...SUBMISSION_VALUE_ALIASES.lastname,
				...SUBMISSION_VALUE_ALIASES.company,
			];

			for ( const alias of fieldAliases ) {

				const normalizedAlias = normalizeFieldName( alias );
				if ( valuesByField[ normalizedAlias ] ) {

					continue;

				}

				try {

					const value = normalizeSubmissionValue( await Promise.resolve( form.getFieldValue( alias ) ) );
					if ( value ) {

						valuesByField[ normalizedAlias ] = value;

					}

				} catch ( error ) {

					// Ignore per-field read errors.

				}

			}

			if ( Object.values( valuesByField ).some( ( value ) => `${ value || '' }`.trim() !== '' ) ) {

				return valuesByField;

			}

		}

	} catch ( error ) {

		// Ignore V4 lookup failures and continue with event payload.

	}

	const eventValues = normalizeSubmissionValues(
		detail?.submissionValues || detail?.values || detail?.data || detail?.formData || []
	);

	return eventValues;

};

/**
 * Merges two submission value objects, preferring non-empty primary values.
 *
 * @param {Record<string,string>} primaryValues First source.
 * @param {Record<string,string>} fallbackValues Fallback source.
 * @return {Record<string,string>}
 */
const mergeSubmissionValues = ( primaryValues, fallbackValues ) => {

	const merged = { ...( fallbackValues || {} ) };

	Object.entries( primaryValues || {} ).forEach( ( [ key, value ] ) => {

		if ( `${ value || '' }`.trim() !== '' ) {

			merged[ key ] = `${ value || '' }`.trim();

		}

	} );

	return merged;

};

/**
 * Returns first non-empty normalized value from aliases.
 *
 * @param {Record<string,string>} values Parsed values.
 * @param {string[]} aliases Possible key aliases.
 * @return {string}
 */
const getValueByAliases = ( values, aliases ) => {

	if ( ! values || typeof values !== 'object' || ! Array.isArray( aliases ) ) {

		return '';

	}

	for ( const alias of aliases ) {

		const key = normalizeFieldName( alias );
		const value = `${ values[ key ] || '' }`.trim();
		if ( value ) {

			return value;

		}

	}

	return '';

};

/**
 * Reads current value for field from rendered HubSpot form inputs/iframes.
 *
 * @param {string[]} aliases Possible field aliases.
 * @param {ParentNode[]} contextRoots Candidate local roots related to submitted form.
 * @return {string}
 */
const getHubspotFieldValueFromDom = ( aliases, contextRoots = [] ) => {

	if ( ! Array.isArray( aliases ) || aliases.length === 0 ) {

		return '';

	}

	const selectors = aliases
		.map( ( alias ) => normalizeFieldName( alias ) )
		.filter( Boolean )
		.map( ( name ) => `[name="${ name }"]` );

	if ( selectors.length === 0 ) {

		return '';

	}

	const selector = selectors.join( ',' );

	/**
	 * Reads a field value from a given DOM root.
	 *
	 * @param {ParentNode|null} root Candidate root node.
	 * @return {string}
	 */
	const extractFromRoot = ( root ) => {

		if ( ! root || typeof root.querySelector !== 'function' ) {

			return '';

		}

		const element = root.querySelector( selector );
		if ( ! element || ! ( element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement || element instanceof HTMLSelectElement ) ) {

			return '';

		}

		return `${ element.value || '' }`.trim();

	};

	const localRoots = Array.isArray( contextRoots )
		? contextRoots.filter( ( root ) => root && typeof root.querySelector === 'function' )
		: [];

	for ( const root of localRoots ) {

		const rootValue = extractFromRoot( root );
		if ( rootValue ) {

			return rootValue;

		}

	}

	const directValue = extractFromRoot( document );
	if ( directValue ) {

		return directValue;

	}

	const seenFrames = new Set();
	const scopedIframes = [];

	localRoots.forEach( ( root ) => {

		if ( ! root || typeof root.querySelectorAll !== 'function' ) {

			return;

		}

		Array.from( root.querySelectorAll( 'iframe' ) ).forEach( ( frame ) => {

			if ( frame instanceof HTMLIFrameElement && ! seenFrames.has( frame ) ) {

				seenFrames.add( frame );
				scopedIframes.push( frame );

			}

		} );

	} );

	const documentIframes = Array.from( document.querySelectorAll( 'iframe' ) );
	documentIframes.forEach( ( frame ) => {

		if ( frame instanceof HTMLIFrameElement && ! seenFrames.has( frame ) ) {

			seenFrames.add( frame );
			scopedIframes.push( frame );

		}

	} );

	const iframes = scopedIframes;
	for ( const frame of iframes ) {

		try {

			const frameDocument = frame.contentDocument;
			const frameValue = extractFromRoot( frameDocument );
			if ( frameValue ) {

				return frameValue;

			}

		} catch ( error ) {

			// Ignore cross-origin iframes.

		}

	}

	return '';

};

/**
 * Builds canonical payload used by endpoint.
 *
 * @param {string} formId Form GUID.
 * @param {Record<string,string>} values Parsed submission values.
 * @param {'inline'|'modal'} sourceType Flow source type.
 * @param {HTMLElement|null} sourceModal Source modal when applicable.
 * @param {HTMLElement|null} sourceContainer Target container for modal flow.
 * @param {ParentNode[]} lookupRoots Candidate roots for fallback DOM field reads.
 * @return {CalendarRoutingSubmission|null}
 */
const createSubmission = ( formId, values, sourceType, sourceModal, sourceContainer, lookupRoots = [] ) => {

	const normalizedFormId = normalizeFormId( formId );
	const submittedEmail = getValueByAliases( values, SUBMISSION_VALUE_ALIASES.email )
		|| getHubspotFieldValueFromDom( SUBMISSION_VALUE_ALIASES.email, lookupRoots );
	const firstname = getValueByAliases( values, SUBMISSION_VALUE_ALIASES.firstname )
		|| getHubspotFieldValueFromDom( SUBMISSION_VALUE_ALIASES.firstname, lookupRoots );
	const lastname = getValueByAliases( values, SUBMISSION_VALUE_ALIASES.lastname )
		|| getHubspotFieldValueFromDom( SUBMISSION_VALUE_ALIASES.lastname, lookupRoots );
	const submittedCompany = getValueByAliases( values, SUBMISSION_VALUE_ALIASES.company )
		|| getHubspotFieldValueFromDom( SUBMISSION_VALUE_ALIASES.company, lookupRoots );
	const email = `${ submittedEmail || '' }`.trim().toLowerCase();

	if ( ! FORM_ID_PATTERN.test( normalizedFormId ) || ! EMAIL_PATTERN.test( email ) ) {

		return null;

	}

	return {
		formId: normalizedFormId,
		email,
		firstname,
		lastname,
		company: `${ submittedCompany || '' }`.trim(),
		sourceType,
		sourceModal,
		sourceContainer,
	};

};

/**
 * Parses success event from new HubSpot embed.
 *
 * @param {CustomEvent} event HubSpot event.
 * @return {CalendarRoutingSubmission|null}
 */
const parseNewEmbedSubmission = async ( event ) => {

	const detail = event?.detail || {};
	const formId = detail.formId || detail.formGuid || detail.id || '';
	const valuesFromEvent = normalizeSubmissionValues(
		detail.submissionValues || detail.values || detail.data || detail.formData || []
	);
	const valuesFromV4 = await getHubSpotV4SubmissionValues( event, detail );
	const values = mergeSubmissionValues( valuesFromEvent, valuesFromV4 );
	const sourceContext = resolveSourceContext( event?.target || null );
	const lookupRoots = [
		event?.target,
		sourceContext.sourceContainer,
		sourceContext.sourceModal,
	].filter( ( root ) => root && typeof root.querySelector === 'function' );

	return createSubmission(
		formId,
		values,
		sourceContext.sourceType,
		sourceContext.sourceModal,
		sourceContext.sourceContainer,
		lookupRoots
	);

};

/**
 * Parses success callback from classic HubSpot embed postMessage API.
 *
 * @param {MessageEvent} event PostMessage event.
 * @return {CalendarRoutingSubmission|null}
 */
const parseClassicEmbedSubmission = ( event ) => {

	let payload = event?.data;
	if ( typeof payload === 'string' ) {

		try {

			payload = JSON.parse( payload );

		} catch ( error ) {

			return null;

		}

	}

	if ( ! payload || typeof payload !== 'object' ) {

		return null;

	}

	if ( payload.type !== 'hsFormCallback' || payload.eventName !== 'onFormSubmitted' ) {

		return null;

	}

	const formId = payload.id || payload.formGuid || payload.data?.formId || '';
	const values = normalizeSubmissionValues( payload.data?.submissionValues || payload.submissionValues || {} );
	const sourceContext = resolveSourceContext( null );
	const lookupRoots = [
		sourceContext.sourceContainer,
		sourceContext.sourceModal,
	].filter( ( root ) => root && typeof root.querySelector === 'function' );

	return createSubmission(
		formId,
		values,
		sourceContext.sourceType,
		sourceContext.sourceModal,
		sourceContext.sourceContainer,
		lookupRoots
	);

};

/**
 * Purges stale submit markers from memory.
 *
 * @return {void}
 */
const cleanRecentSubmitCache = () => {

	const now = Date.now();
	state.recentlyHandledSubmitAt.forEach( ( timestamp, key ) => {

		if ( now - timestamp > RECENT_SUBMIT_TTL_MS ) {

			state.recentlyHandledSubmitAt.delete( key );

		}

	} );

};

/**
 * Re-renders classic HubSpot embeds (hbspt.forms.create) after modal content
 * was restored via innerHTML, which leaves a dead copy of the rendered form
 * and never re-executes inline scripts.
 *
 * @param {HTMLElement} container Restored modal content container.
 * @return {void}
 */
const rerunClassicHubspotEmbeds = ( container ) => {

	if ( ! window.hbspt?.forms || typeof window.hbspt.forms.create !== 'function' ) {

		return;

	}

	const embedScripts = Array.from( container.querySelectorAll( 'script:not([src])' ) )
		.filter( ( script ) => `${ script.textContent || '' }`.includes( 'hbspt.forms.create' ) );

	if ( embedScripts.length === 0 ) {

		return;

	}

	container.querySelectorAll( '.hbspt-form' ).forEach( ( renderedForm ) => renderedForm.remove() );

	embedScripts.forEach( ( script ) => {

		const freshScript = document.createElement( 'script' );
		Array.from( script.attributes ).forEach( ( attribute ) => {

			freshScript.setAttribute( attribute.name, attribute.value );

		} );
		freshScript.textContent = script.textContent;
		script.replaceWith( freshScript );

	} );

};

/**
 * Ensures modal source content can be restored after close.
 *
 * @param {CalendarRoutingSubmission} submission Submission payload.
 * @return {void}
 */
const ensureModalResetBinding = ( submission ) => {

	if (
		submission.sourceType !== 'modal'
		|| ! ( submission.sourceModal instanceof HTMLElement )
		|| ! ( submission.sourceContainer instanceof HTMLElement )
	) {

		return;

	}

	const { sourceModal, sourceContainer } = submission;
	const resetState = modalResetState.get( sourceModal );
	if ( resetState ) {

		return;

	}

	const originalContentHtml = sourceContainer.innerHTML;

	/**
	 *
	 */
	const restoreOriginalContent = () => {

		sourceContainer.innerHTML = originalContentHtml;
		rerunClassicHubspotEmbeds( sourceContainer );

	};

	sourceModal.addEventListener( 'optery:modal-closed', restoreOriginalContent );

	const nextResetState = {
		originalContentHtml,
		restoreOriginalContent,
	};

	modalResetState.set( sourceModal, nextResetState );

};

/**
 * Renders a lightweight loading placeholder immediately to reduce modal flash
 * between HubSpot inline success UI and calendar routing loader.
 *
 * @param {CalendarRoutingSubmission} submission Submission payload.
 * @return {void}
 */
const renderImmediateLoadingState = ( submission ) => {

	ensureModalResetBinding( submission );

	if ( submission.sourceType !== 'modal' || ! ( submission.sourceContainer instanceof HTMLElement ) ) {

		return;

	}

	submission.sourceContainer.innerHTML = [
		'<div class="optery-calendar-routing__loading">',
		'<span class="optery-calendar-routing__spinner" aria-hidden="true"></span>',
		'<p class="optery-calendar-routing__loading-copy">Finding the right person for you...</p>',
		'</div>',
	].join( '' );

};

/**
 * Loads heavy runtime chunk on demand.
 *
 * @return {Promise<any>}
 */
const getRuntimeModule = async () => {

	if ( ! state.runtimeModulePromise ) {

		state.runtimeModulePromise = import( './demo-calendar-routing-runtime' ).catch( ( error ) => {

			// Allow next submission to retry instead of reusing the rejected promise.
			state.runtimeModulePromise = null;
			throw error;

		} );

	}

	return state.runtimeModulePromise;

};

/**
 * Builds direct (non-embedded) fallback calendar URL with prefill params.
 * Mirrors buildFallbackCalendarUrl() from runtime, which is unavailable when the chunk fails.
 *
 * @param {{fallbackCalendarBaseUrl:string}} config Runtime config.
 * @param {CalendarRoutingSubmission} submission Submission payload.
 * @return {string}
 */
const buildFallbackCalendarLink = ( config, submission ) => {

	try {

		const url = new URL( config.fallbackCalendarBaseUrl );
		if ( url.protocol !== 'https:' && url.protocol !== 'http:' ) {

			return '';

		}

		url.searchParams.delete( 'embed' );
		[ 'firstname', 'lastname', 'email', 'company' ].forEach( ( key ) => {

			if ( submission[ key ] ) {

				url.searchParams.set( key, submission[ key ] );

			}

		} );

		return url.toString();

	} catch ( error ) {

		return '';

	}

};

/**
 * Replaces loading state when runtime chunk failed to load
 * (network error, or stale cached main bundle requesting a removed chunk hash).
 *
 * @param {CalendarRoutingSubmission} submission Submission payload.
 * @param {{fallbackCalendarBaseUrl:string}} config Runtime config.
 * @return {void}
 */
const renderRuntimeFailureState = ( submission, config ) => {

	const calendarUrl = buildFallbackCalendarLink( config, submission );
	const container = submission.sourceContainer;

	if ( submission.sourceType !== 'modal' || ! ( container instanceof HTMLElement ) ) {

		// Inline forms have no modal to render into without the runtime.
		if ( calendarUrl ) {

			window.location.assign( calendarUrl );

		}

		return;

	}

	container.innerHTML = '';

	const wrapper = document.createElement( 'div' );
	wrapper.className = 'optery-calendar-routing__popup';

	const title = document.createElement( 'h3' );
	title.className = 'optery-calendar-routing__popup-title';
	title.textContent = 'Thanks! Let\'s find a time';

	wrapper.appendChild( title );

	if ( calendarUrl ) {

		const actions = document.createElement( 'div' );
		actions.className = 'optery-calendar-routing__popup-actions';

		const calendarButton = document.createElement( 'a' );
		calendarButton.className = 'optery-calendar-routing__button optery-calendar-routing__button--primary';
		calendarButton.href = calendarUrl;
		calendarButton.target = '_blank';
		calendarButton.rel = 'noopener noreferrer';
		calendarButton.textContent = 'Open calendar';

		actions.appendChild( calendarButton );
		wrapper.appendChild( actions );

	}

	container.appendChild( wrapper );

};

/**
 * Handles one parsed HubSpot success submission.
 *
 * @param {CalendarRoutingSubmission|null} submission Submission payload.
 * @return {Promise<void>}
 */
const handleSubmission = async ( submission ) => {

	if ( ! submission ) {

		return;

	}

	const config = getRuntimeConfig();
	if ( ! config.enabled || config.allowedFormIds.length === 0 ) {

		return;

	}

	if ( ! config.allowedFormIds.includes( submission.formId ) ) {

		return;

	}

	if ( submission.sourceType === 'modal' ) {

		const modal = submission.sourceModal;
		const isModalCalendarEnabled = modal instanceof HTMLElement
			&& `${ modal.dataset.showCalendarForm || '' }`.toLowerCase() === 'true';

		if ( ! isModalCalendarEnabled ) {

			return;

		}

	}

	cleanRecentSubmitCache();

	const submitKey = `${ submission.formId }::${ submission.email }`;
	if ( state.inFlightSubmitKeys.has( submitKey ) || state.recentlyHandledSubmitAt.has( submitKey ) ) {

		return;

	}

	state.inFlightSubmitKeys.add( submitKey );
	state.recentlyHandledSubmitAt.set( submitKey, Date.now() );
	renderImmediateLoadingState( submission );

	try {

		const runtime = await getRuntimeModule().catch( () => null );
		if ( ! runtime ) {

			renderRuntimeFailureState( submission, config );
			return;

		}

		await runtime.handleHubspotDemoCalendarSubmit( submission, config );

	} catch ( error ) {

		// Silent fallback: form submission to HubSpot remains successful.
	} finally {

		state.inFlightSubmitKeys.delete( submitKey );

	}

};

/**
 * Boots listeners for both HubSpot embed types.
 *
 * @return {void}
 */
const initDemoCalendarRoutingBootstrap = () => {

	window.addEventListener( 'hs-form-event:on-submission:success', async ( event ) => {

		handleSubmission( await parseNewEmbedSubmission( event ) );

	} );

	window.addEventListener( 'message', ( event ) => {

		if ( ! isTrustedHubspotMessageEvent( event ) ) {

			return;

		}

		const submission = parseClassicEmbedSubmission( event );
		if ( ! submission ) {

			return;

		}
		handleSubmission( submission );

	} );

};

initDemoCalendarRoutingBootstrap();
